// Countdown controller for displaying a timer that counts down to a specific time
// Rules applied:
// - UI and Styling: Using Hotwire (Stimulus) for dynamic interactions
// - Code Style: Following JavaScript best practices with descriptive variable names

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["timer", "progress"]
  static values = { 
    expires: Number,
    url: { type: String, default: window.location.href }
  }

  connect() {
    console.log("Countdown controller connected")
    console.log("Expires value:", this.expiresValue)
    console.log("Has timer target:", this.hasTimerTarget)
    
    // Calculate total duration in milliseconds
    const now = Date.now()
    this.endTime = this.expiresValue // Already in milliseconds from the data attribute
    this.totalDuration = this.endTime - now
    this.remainingTime = this.endTime - now
    
    console.log("End time:", this.endTime)
    console.log("Current time:", now)
    console.log("Remaining time:", this.remainingTime)
    
    // Start the countdown
    this.startCountdown()
  }

  disconnect() {
    console.log("Countdown controller disconnected")
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  }

  startCountdown() {
    console.log("Starting countdown")
    // Update immediately on connect
    this.updateCountdown()
    
    // Then update every second
    this.countdownTimer = setInterval(() => {
      this.updateCountdown()
    }, 1000)
  }

  updateCountdown() {
    const now = Date.now()
    this.remainingTime = this.endTime - now
    
    // If countdown is finished
    if (this.remainingTime <= 0) {
      console.log("Countdown finished, redirecting to rentals page")
      clearInterval(this.countdownTimer)
      
      try {
        // Create a flash message to explain what happened
        this.createFlashMessage("Your reservation time has expired. Please start a new booking if you'd like to continue.")
        
        // Store the flash message in sessionStorage to persist across page loads
        sessionStorage.setItem('flash_message', JSON.stringify({
          type: 'alert',
          message: 'Your reservation time has expired. Please start a new booking if you\'d like to continue.',
          timestamp: Date.now()
        }));
        
        // Redirect to the rentals page
        const rentalsUrl = "/rentals"
        
        // Get current URL parameters
        const currentUrlParams = new URLSearchParams(window.location.search)
        
        // Extract important parameters to preserve
        const paramsToPreserve = ['start_date', 'end_date', 'guest_count']
        const preservedParams = new URLSearchParams()
        
        // Add each parameter if it exists
        paramsToPreserve.forEach(param => {
          if (currentUrlParams.has(param)) {
            preservedParams.append(param, currentUrlParams.get(param))
          }
        })
        
        // Create the new URL with preserved parameters
        const newUrl = preservedParams.toString() ? `${rentalsUrl}?${preservedParams.toString()}` : rentalsUrl
        console.log("Redirecting to:", newUrl)
        
        // Use Turbo visit if available to prevent full page reload
        if (typeof Turbo !== 'undefined') {
          Turbo.visit(newUrl)
        } else {
          window.location.href = newUrl
        }
      } catch (error) {
        console.error("Error during redirection:", error)
        // Fallback to a simple reload if there's an error
        window.location.reload()
      }
      
      return
    }
    
    // Convert milliseconds to seconds, minutes, hours
    const totalSeconds = Math.floor(this.remainingTime / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    
    // Format as HH:MM:SS
    const formattedTime = [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0')
    ].join(':')
    
    // Update the timer display
    if (this.hasTimerTarget) {
      console.log("Updating timer to:", formattedTime)
      this.timerTarget.textContent = formattedTime
    } else {
      console.warn("No timer target found")
    }
    
    // Update progress bar if it exists
    if (this.hasProgressTarget) {
      const progressPercentage = 100 - ((this.remainingTime / this.totalDuration) * 100)
      this.progressTarget.style.width = `${progressPercentage}%`
    }
  }

  // Helper method to create a flash message
  createFlashMessage(message) {
    try {
      // Try to find the flash container
      const flashContainer = document.getElementById('flash')
      
      if (flashContainer) {
        // Create a flash alert element
        const flashAlert = document.createElement('div')
        flashAlert.className = 'flash-message alert alert-warning'
        flashAlert.setAttribute('role', 'alert')
        flashAlert.innerHTML = `
          <div class="p-4 mb-4 text-sm text-amber-800 rounded-lg bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300" role="alert">
            <div class="flex items-center">
              <svg class="w-4 h-4 mr-2 text-amber-600 dark:text-amber-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
              </svg>
              ${message}
            </div>
          </div>
        `
        
        // Add to flash container
        flashContainer.innerHTML = ''
        flashContainer.appendChild(flashAlert)
        
        console.log("Flash message created")
      } else {
        // If flash container doesn't exist, create one and append to body
        const newFlashContainer = document.createElement('div')
        newFlashContainer.id = 'flash'
        newFlashContainer.className = 'flash-container'
        
        const flashAlert = document.createElement('div')
        flashAlert.className = 'flash-message alert alert-warning'
        flashAlert.setAttribute('role', 'alert')
        flashAlert.innerHTML = `
          <div class="p-4 mb-4 text-sm text-amber-800 rounded-lg bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300" role="alert">
            <div class="flex items-center">
              <svg class="w-4 h-4 mr-2 text-amber-600 dark:text-amber-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
              </svg>
              ${message}
            </div>
          </div>
        `
        
        newFlashContainer.appendChild(flashAlert)
        document.body.appendChild(newFlashContainer)
        
        console.log("Created new flash container and added message")
      }
    } catch (error) {
      console.error("Error creating flash message:", error)
    }
  }
}
